<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { UserModel } from '@/models/user';
import Dialog from 'primevue/dialog';
import { useStudyStore } from '@/stores/study';
import { useFormStore } from '@/stores/forms';
import { useUserManagementStore } from '@/stores/userManagement';
import UserInviteRow from './UserInviteRow.vue';
import { ButtonType, CustomButton } from '@/components';

const studyStore = useStudyStore();
const formStore = useFormStore();
const userManagementStore = useUserManagementStore();

const dialogTitle = computed(() => studyStore.selectedStudy?.title);

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const emits = defineEmits(['onClose']);

onMounted(() => {
  const allForms = formStore.formList.map(form => form.base64Id) as string[];
  userManagementStore.userInvites = [UserModel.createEmpty(allForms)];
});

const closeDialog = () => {
  emits('onClose');
};

const addAnotherInvite = () => {
  const allForms = formStore.formList.map(form => form.base64Id) as string[];
  const userInvite = UserModel.createEmpty(allForms, true);
  userManagementStore.userInvites.push(userInvite);
};

const onDeleteInvite = (invite: UserModel) => {
  const inviteIndex = userManagementStore.userInvites.findIndex(
    user => user.identifier === invite.identifier
  );
  if (inviteIndex > -1) {
    userManagementStore.userInvites.splice(inviteIndex, 1);
  }
};

const submiteInvites = async () => {
  await userManagementStore.inviteUserToStudy(userManagementStore.userInvites);
  closeDialog();
};

const updateInvite = (updatedInvite: UserModel) => {
  const index = userManagementStore.userInvites.findIndex(
    invite => invite.identifier === updatedInvite.identifier
  );
  if (index !== -1) {
    userManagementStore.userInvites[index] = updatedInvite;
  }
};

const enableInviteButton = computed(() => {
  return userManagementStore.userInvites.some(
    invite => invite.email !== '' && !invite.invalidEmail
  );
});

const onEmailPaste = (text: string, invite: UserModel) => {
  const emails = text.split('\n');
  const updatedInvites = addEmails(
    emails,
    invite.identifier,
    userManagementStore.userInvites
  );
  userManagementStore.userInvites = [];
  setTimeout(() => {
    userManagementStore.userInvites = updatedInvites;
  }, 1);
};

const addEmails = (emails: string[], currentId: string, list: UserModel[]) => {
  let identifier: string | null = currentId;
  emails.forEach(email => {
    let hasRow = false;
    for (const user of list) {
      if (user.identifier === identifier) {
        hasRow = true;
        user.email = email.trim();
        identifier = null;
        break;
      }
    }
    if (!hasRow) {
      const allForms = formStore.formList.map(
        form => form.base64Id
      ) as string[];
      const userInvite = UserModel.createEmpty(allForms, true);
      userInvite.email = email;
      list.push(userInvite);
    }
  });
  return list;
};
</script>

<template>
  <Dialog :visible="visible"
    modal
    class="dialog"
    :draggable="false"
    @update:visible="closeDialog">
    <template #header>
      <h2 class="dialog-title">
        Invite Collaborators to <span>{{ dialogTitle }}</span>
      </h2>
    </template>
    <div class="invite-table">
      <template v-for="invite in userManagementStore.userInvites"
        :key="invite.identifier">
        <UserInviteRow :invite="invite as UserModel"
          @onDelete="onDeleteInvite"
          @updateInvite="updateInvite"
          @onEmailPaste="onEmailPaste"/>
      </template>
    </div>
    <CustomButton text="Add another"
      @onClick="addAnotherInvite"
      :margin-left="16"
      :icon="{ icon: 'plus', style: 'fas' }"
      :type="ButtonType.OUTLINED_PRIMARY"/>
    <div class="dialog-buttons">
      <CustomButton text="Cancel"
        @onClick="closeDialog"
        :type="ButtonType.OUTLINED"/>
      <CustomButton text="Send invitation"
        :margin-left="16"
        :loading="userManagementStore.isInvitingUser.isLoading"
        :type="ButtonType.POSITIVE"
        :disabled="!enableInviteButton"
        @onClick="submiteInvites"/>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
.dialog-title {
  span {
    font-weight: 600;
  }
}
.invite-table {
  thead {
    th {
      padding: 12px;
      font-weight: 600;
    }
  }
}
.dialog-buttons {
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
}
</style>
